import { ToolbarNav } from '../../features/toolbarNav/views';
import { MobileNotificationView } from './MobileNotificationView';
import { pendoTrack } from '../../../backbone-unorganized/utils/logging';
import { waitOrientationChangeToLandscape } from '../../../shared/utils/deviceOrientation';

/**
 * A dummy view that displays nothing. Used for any fields we don't want to show.
 */
export const EmptyView = Backbone.Marionette.View.extend();

export const BaseView = Backbone.Marionette.CompositeView.extend({
    on_orientation_change: function () {}, // callers look for this, no-op or override
    on_resume: function () {}
});

export const MobileBaseModal = BaseView.extend({
    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this.listenTo($f.application.vent, 'app:orientation_change', async () => {
            await waitOrientationChangeToLandscape();
            this.on_orientation_change();
        });
    }
});

/**
 * Base view that includes a ToolbarNav. Views that inherit from this should include a `.toolbarnav` element in their HTML and
 * define the title that will be shown as `this.toolbarnav_title`. The toolbarnav view can be accessed from `this.toolbarnav`
 * after it's been created. To further manipulate the toolbarnav after it's been created, use `this.on_toolbarnav_init`.
 *
 * @see ToolbarNav
 */
export const BaseToolbarNavPage = BaseView.extend({
    toolbarnav: null,
    toolbarnav_class: ToolbarNav, // subviews can specify a different toolbarnav if necessary
    toolbarnav_title: null,
    toolbarnav_subtitle: null,
    toolbarnav_actions: null, // actions to be passed to the toolbarnav. Can add dynamically with ToolbarNav.add_actions().
    toolbarnav_show_add_task: true, // should the toolbarnav show the add task button?
    toolbarnav_show_edit_sensor: false, // should the toolbarnav show the edit sensor button?
    toolbarnav_create_task_options: null, // {} or function - extra options to send to ProjectCreator if the user chooses to add a task
    toolbarnav_center_title: false, // set as true if the title should be centered

    notification: null, // MobileNotificationView
    use_transitions: false,
    allow_reverse_transitions: true,
    is_top_level_page: false, // set to true if the view is meant to be accessed from the leftnav and should be slide-open-able
    is_historic_view: false, // passed in from $f.mobile.nav / TransitionRegion.show()
    auto_resize_page_scrollable: true, // if true, automatically calculate and apply the height for .page_scrollable.
    // Subclasses that implement multiple tabs, etc. may want to handle this manually in another place

    _base_toolbarnav_page_class: null, // @todo @author:david this is just for code cleanliness when calling super while we have both a
    // BaseToolbarNavPage and BaseToolbarNavPageLayout.

    constructor: function () {
        return BaseToolbarNavPageLayout.prototype.constructor.apply(this, arguments);
    },

    render: function () {
        return BaseToolbarNavPageLayout.prototype.render.apply(this, arguments);
    },

    init_toolbarnav: function () {
        return BaseToolbarNavPageLayout.prototype.init_toolbarnav.apply(this, arguments);
    },

    onShow: function () {
        return BaseToolbarNavPageLayout.prototype.onShow.apply(this, arguments);
    },

    // Subclasses override this to add extra setup to toolbarnav
    on_toolbarnav_init: function () {
        return BaseToolbarNavPageLayout.prototype.on_toolbarnav_init.apply(this, arguments);
    },

    on_orientation_change: function () {
        return BaseToolbarNavPageLayout.prototype.on_orientation_change.apply(this, arguments);
    },

    scroll_to_element: function () {
        return BaseToolbarNavPageLayout.prototype.scroll_to_element.apply(this, arguments);
    },

    go_back: function () {
        return BaseToolbarNavPageLayout.prototype.go_back.apply(this, arguments);
    },

    show_back_button: function () {
        return BaseToolbarNavPageLayout.prototype.show_back_button.apply(this, arguments);
    }
});

// @todo @author:david convert all views that use BaseToolbarNavPage to use this Layout!
export const BaseToolbarNavPageLayout = Backbone.Marionette.Layout.extend({
    toolbarnav: null,
    toolbarnav_class: ToolbarNav, // subviews can specify a different toolbarnav if necessary
    toolbarnav_title: null,
    toolbarnav_subtitle: null,
    toolbarnav_actions: null, // actions to be passed to the toolbarnav. Can add dynamically with ToolbarNav.add_actions().
    toolbarnav_select_options: null, // if you want to have select options in the view header, set them here
    toolbarnav_show_add_task: true, // should the toolbarnav show the add task button?
    toolbarnav_show_edit_sensor: false, // should the toolbarnav show the edit sensor button?
    toolbarnav_toggle_search: false, // If there is a search bar on the page should we show a button to toggle it?
    // @todo @author:david we should really have a system in place to add ANY button to the toolbarnav,
    // much like we do for toolbarnav_actions and toolbarnav_select_options
    toolbarnav_create_task_options: null, // {} or function - extra options to send to ProjectCreator if the user chooses to add a task
    toolbarnav_center_title: false, // set as true if the title should be centered

    is_modal: false, // children that are "modals" should set this to true to get modal functionality
    notification: null, // MobileNotificationView
    use_transitions: false,
    allow_reverse_transitions: true,
    is_top_level_page: false, // set to true if the view is meant to be accessed from the leftnav and should be slide-open-able
    is_historic_view: false, // passed in from $f.mobile.nav / TransitionRegion.show()
    auto_resize_page_scrollable: true, // if true, automatically calculate and apply the height for .page_scrollable.
    // Subclasses that implement multiple tabs, etc. may want to handle this manually in another place

    _base_toolbarnav_page_class: null, // @todo @author:david this is just for code cleanliness when calling super while we have both a
    // BaseToolbarNavPage and BaseToolbarNavPageLayout.

    constructor: function (options) {
        options = options || {};
        var this_ = this;

        // Once we get rid of BaseToolbarNavPage, this can be a plain super() call as normal. Until then:
        if (this instanceof BaseToolbarNavPageLayout) {
            this._base_toolbarnav_page_class = BaseToolbarNavPageLayout;
        } else if (this instanceof BaseToolbarNavPage) {
            this._base_toolbarnav_page_class = BaseToolbarNavPage;
        }

        if (this.is_modal || (options && options.is_modal)) {
            this.className = 'dialog typeahead_modal';
            this.toolbarnav_show_add_task = false;
        }

        // Every time we load a new page, let's check to see if we need to reauthenticate with storage.zenput.com
        $f.storage.refresh();

        this._base_toolbarnav_page_class.__super__.constructor.apply(this, arguments);

        this.listenTo($f.application.vent, 'app:orientation_change', function () {
            this_.on_orientation_change();
        });

        // iOS needs us to tell it when the keyboard is open so that we can add spacing behind the keyboard
        // This doesn't seem to be necessary for mobile web on iOS.
        this.listenTo($f.application.vent, 'cordova:keyboard:show cordova:keyboard:hide', function (e) {
            if (!this_.$el) {
                return;
            }

            if (this_.auto_resize_page_scrollable) {
                return $f.mobile.update_page_scrollable_height(this_.$el);
            }

            $f.application.vent.trigger('comments_tab:on_orientation_change');
        });
    },

    // @todo @author:david copied from BaseView
    on_resume: function () {},

    render: function () {
        this._base_toolbarnav_page_class.__super__.render.call(this);

        this.init_toolbarnav();
        // Show the notification element, used by notifications.ts
        if (this.$el.find('.toast_notification').length == 0) {
            this.$el.prepend('<div class="toast_notification"></div>');
        }
        this.notification = new MobileNotificationView({
            el: this.$el.find('.toast_notification').get(0),
            $el: this.$el.find('.toast_notification'),
            parent_page: this
        }).render();

        if (this._isShown) {
            // only call if we're re-rendering. Otherwise, we'll call these in onShow()
            this.notification.on_show();
            this.on_orientation_change();
        }
    },

    init_toolbarnav: function () {
        if (!this.toolbarnav_class) {
            return;
        }

        var this_ = this;

        if (this.toolbarnav) {
            this.toolbarnav.close();
        }

        if (this.$el.find('.toolbarnav').length == 0) {
            this.$el.prepend('<header class="toolbarnav"></header>');
        }

        if ($('html').hasClass('fullscreen')) {
            this.$el.prepend('<div class="fullscreen_filler"></div>'); // must go after .toolbarnav insertion
        }

        // Convert titles from functions to values if necessary
        if (this.page_title && _.isFunction(this.page_title)) {
            this.page_title = this.page_title();
        }
        if (this.page_subtitle && _.isFunction(this.page_subtitle)) {
            this.page_subtitle = this.page_subtitle();
        }

        // @todo @author:david now that this view is a Layout, we can use regions to show the toolbarnav

        this.toolbarnav = new this.toolbarnav_class({
            el: this.$el.find('.toolbarnav').get(0),
            $el: this.$el.find('.toolbarnav'),
            parent_page: this,
            page_title: _.isFunction(this.toolbarnav_title) ? this.toolbarnav_title() : this.toolbarnav_title,
            page_subtitle: _.isFunction(this.toolbarnav_subtitle)
                ? this.toolbarnav_subtitle()
                : this.toolbarnav_subtitle,
            show_back_button: this.show_back_button(),
            actions: this.toolbarnav_actions,
            toolbarnav_select_options: this.toolbarnav_select_options,
            primary_action: this.primary_action,
            show_add_task: this.toolbarnav_show_add_task,
            show_edit_sensor: this.toolbarnav_show_edit_sensor,
            toggle_search: this.toolbarnav_toggle_search,
            create_task_options: this.toolbarnav_create_task_options,
            center_title: this.toolbarnav_center_title
        }).render();

        if (this._isShown) {
            this.toolbarnav.on_show();
        }

        this.listenTo(this.toolbarnav, 'toolbarnav:go_back', function () {
            this_.go_back();
        });

        this.on_toolbarnav_init();
    },

    onShow: function () {
        var $main_content = this.$el.parents('.main_content, #content_overlay'); // .main_content for normal views, native modals; .dialog for web modals

        if (this.$el.find('.search_bar:visible').length > 0) {
            $main_content.addClass('has_search_bar');
        } else {
            $main_content.removeClass('has_search_bar');
        }

        // Pass onShow down to our toolbarnav
        if (this.toolbarnav && this.toolbarnav.on_show) {
            this.toolbarnav.on_show();
        }
        if (this.notification && this.notification.on_show) {
            this.notification.on_show();
        }

        if (this.is_historic_view) {
            $f.mobile.nav.restore_scroll_position();
        }

        this.on_orientation_change();

        const navViews = $f.mobile.nav && $f.mobile.nav._views;
        const topViewClass = navViews?.at(-1);
        const isTopLevelNavigation = topViewClass && this instanceof topViewClass;

        if (this.screen_name && isTopLevelNavigation) {
            const direction = ($f.mobile.nav && $f.mobile.nav.consume_last_nav_direction()) ?? 'forward';

            if ($f.flags.get_flag('pendo_mobile_screen_tracking') && !this._pendo_screen_entered_fired) {
                this._pendo_screen_entered_fired = true;

                const entryPath =
                    navViews && navViews.length >= 2
                        ? navViews[navViews.length - 2]?.prototype?.screen_name ?? 'unknown'
                        : 'session_start';

                pendoTrack('OEm_screen_entered_' + this.screen_name, {
                    entry_path: entryPath,
                    navigation_direction: direction
                });
            }
        }
    },

    // Subclasses override this to add extra setup to toolbarnav
    on_toolbarnav_init: function (speed) {},

    /**
     * Implement specific functionality for whether the navbar should show the back button.
     * Default behavior is to return true if not a top level page
     *
     * @return {Boolean}
     */
    show_back_button: function () {
        return !this.is_top_level_page;
    },

    /**
     * Scroll to an element or a distance from top.
     *
     * @param  {element|number} target      The element to scroll to. This could also be a number for distance from top
     * @param  {number}         speed       How fast should we scroll?
     * @param  {number}         offset      How much should offset should we scroll to?
     */
    scroll_to_element: function (target, speed, offset) {
        $f.mobile.scroll_to_element(this.$el.find('.page_scrollable'), target, speed, offset);
    },

    /**
     * Resize the .page_scrollable div based on the current height of the page.
     *
     * This expects there to be no more than one .page_scrollable element present in the view. We will ignore any
     * elements with .page_scrollable_height_ignore that appear AFTER the .page_scrollable element when calculating
     * the height of .page_scrollable.
     *
     * @param {jQuery Element} $container_el   (optional) a containing div to restrict DOM searches to. This can be
     *                                         useful for pages that have multiple .page_scrollable elements.
     *                                         Defaults to this.$el
     */
    on_orientation_change: function ($container_el) {
        $container_el = $container_el || this.$el;

        // Toggle the search button activity
        if ($container_el.find('.search_bar').height() > 0) {
            $container_el.find('.toggle_search').addClass('active');
        } else {
            $container_el.find('.toggle_search').removeClass('active');
        }

        if (this.auto_resize_page_scrollable) {
            $f.mobile.update_page_scrollable_height($container_el);
        }
    },

    /**
     * Triggered by our Toolbarnav when the user clicks the back button. By default, we'll just call go_back() on the nav.
     */
    go_back: function () {
        if (this.is_modal) {
            this.close();
            return;
        }

        $f.mobile.nav.go_back();
    }
});

/**
 * Base class for BottomSheet menus.
 */
export const BottomSheet = Backbone.Marionette.Layout.extend({
    template: '#tpl_bottom_sheet',
    className: 'bottom_sheet',
    ANIMATION_TIME_MS: 500,
    title: null, // title to be defined by subclass

    events: {
        'click .close_bottom_sheet': 'close'
    },

    regions: {
        r_bottom_sheet_body: '.bottom_sheet_body'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.title) {
            throw new Error('Programmer Error: no title specified for BottomSheet');
        }
    },

    onBeforeRender: function () {
        if (this._isRendered) {
            return;
        } // don't animate in if we're just re-rendering

        this.$el.css('transform', 'translateY(100%)');
    },

    onShow: function () {
        var this_ = this;

        this._animate(true, function () {
            // Now that we've shown the view and it's in place, update our scrollable height to fit the size
            // of its content (with a max-height of half the screen from CSS).
            setTimeout(function () {
                // setTimeout prevents flicker on Android for longer sections list
                this_.$el
                    .find('.page_scrollable')
                    .css('height', $(window).outerHeight() - this_.$el.find('.page_scrollable').offset().top);
            }, 10);
        });

        $f.mobile.nav.show_modal_touchblock({ duration: this.ANIMATION_TIME_MS }, function () {
            this_.close();
        });
    },

    _animate: function (is_show, callback) {
        callback = callback || function () {};

        this.$el.velocity(
            {
                translateY: is_show ? ['0%', '100%'] : ['100%', '0%'] // to, from
            },
            {
                duration: this.ANIMATION_TIME_MS,
                easing: 'easeOutCirc',
                complete: callback
            }
        );
    },

    close: function () {
        var this_ = this;
        var arguments_ = arguments;

        $f.mobile.nav.hide_modal_touchblock({ duration: this.ANIMATION_TIME_MS });

        this._animate(false, function () {
            BottomSheet.__super__.close.apply(this_, arguments_);
        });

        return false;
    }
});
